package co.maytes.standupbot.dashboard.styles

import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.percent

object HomeClasses {
    const val ROOT = "hm-root"
    const val PANEL = "hm-panel"
    const val LIFT = "hm-lift"
    const val PAD = "hm-pad"
    const val HERO = "hm-hero"
    const val HERO_COPY = "hm-hero-copy"
    const val ART = "hm-art"
    const val STATS = "hm-stats"
    const val STAT = "hm-stat"
    const val STAT_VALUE = "hm-stat-value"
    const val FLOW = "hm-flow"
    const val FLOW_INPUTS = "hm-flow-inputs"
    const val NODE = "hm-node"
    const val NODE_ON = "hm-node--on"
    const val NODE_OFF = "hm-node--off"
    const val NODE_HUB = "hm-node--hub"
    const val CHIP = "hm-chip"
    const val LABEL = "hm-label"
    const val CONNECT = "hm-connect"
    const val DOT = "hm-dot"
    const val DOT_OK = "hm-dot--ok"
    const val DOT_WARN = "hm-dot--warn"
    const val DOT_BAD = "hm-dot--bad"
    const val WIRES = "hm-wires"
    const val WIRE = "hm-wire"
    const val WIRE_ON = "hm-wire--on"
    const val WIRE_OFF = "hm-wire--off"
    const val WIRE_FLOW = "hm-wire-flow"
    const val SETUP = "hm-setup"
    const val SETUP_SIDE = "hm-setup-side"
    const val RING = "hm-ring"
    const val RING_TRACK = "hm-ring-track"
    const val RING_FILL = "hm-ring-fill"
    const val RING_LABEL = "hm-ring-label"
    const val MILESTONES = "hm-milestones"
    const val MILESTONE = "hm-ms"
    const val MILESTONE_ACTIVE = "hm-ms--active"
    const val ICON_TILE = "hm-icon-tile"
    const val STEP = "hm-step"
    const val MARK = "hm-mark"
    const val MARK_DONE = "hm-mark--done"
    const val MARK_TODO = "hm-mark--todo"
    const val MARK_BAD = "hm-mark--bad"
    const val MARK_WARN = "hm-mark--warn"
    const val MARK_OPTIONAL = "hm-mark--optional"
    const val LIVE = "hm-live"
    const val SPAN_4 = "hm-s4"
    const val SPAN_5 = "hm-s5"
    const val SPAN_7 = "hm-s7"
    const val SPAN_12 = "hm-s12"
    const val CARD_HEAD = "hm-card-head"
    const val BIG_TIME = "hm-big-time"
    const val DAYS = "hm-days"
    const val DAY = "hm-day"
    const val DAY_ON = "hm-day--on"
    const val RUN = "hm-run"
    const val BARS = "hm-bars"
    const val BAR = "hm-bar"
    const val BAR_FILL = "hm-bar-fill"
    const val BAR_ZERO = "hm-bar-fill--zero"
    const val BAR_TODAY = "hm-bar-fill--today"
    const val STACK = "hm-stack"
    const val STACK_ITEM = "hm-stack-item"
    const val STACK_MORE = "hm-stack-more"
    const val GHOST_AVATAR = "hm-ghost-avatar"
    const val TILES = "hm-tiles"
    const val TILE = "hm-tile"
    const val EMPTY = "hm-empty"
    const val EMPTY_ART = "hm-empty-art"
    const val GHOST = "hm-ghost"
    const val GHOST_ACCENT = "hm-ghost--accent"
    const val BONE = "hm-bone"
    const val STRIP = "hm-strip"
    const val STRIP_END = "hm-strip-end"
    const val STEP_TEXT = "hm-step-text"
    const val WIRE_CELL = "hm-wire-cell"
    const val WIRE_CELL_OUT = "hm-wire-cell--out"
    const val CHANNEL = "hm-channel"
    const val TILE_LABEL = "hm-tile-label"
    const val ROW_TEXT = "hm-row-text"
    const val STACK_FACTS = "hm-facts"
}

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(selector: String, query: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { cssRule(CSSMediaQuery.Raw(query), null) { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.hoverRule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { hoverFinePointer { Modifier.styleModifier(build) } }
}

private fun tint(share: String): String = "color-mix(in srgb, var(${HomeArt.TINT_VAR}) $share, transparent)"

private fun mix(color: String, share: String): String = "color-mix(in srgb, $color $share, transparent)"

private fun StyleScope.lift() {
    property("transform", "translateY(${Marketing.LIFT_DISTANCE})")
    property("box-shadow", Shadow.MD)
}

private fun StyleScope.flexColumn(gap: String) {
    property("display", "flex")
    property("flex-direction", "column")
    property("gap", gap)
}

private fun StyleScope.centered() {
    property("display", "grid")
    property("place-items", "center")
}

private fun StyleScope.truncate() {
    property("overflow", "hidden")
    property("text-overflow", "ellipsis")
    property("white-space", "nowrap")
}

private fun StyleScope.washedArt() {
    property(
        "background-image",
        "radial-gradient(circle at 18% 12%, ${tint(HomeArt.TINT_GLOW)}, transparent 62%), " +
            "radial-gradient(circle at 88% 92%, ${mix(Palette.accent.cssRgb(), HomeArt.TINT_SOFT)}, transparent 58%)",
    )
}

@InitSilk
fun initHomeStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerKeyframes("hm-flow") {
        0.percent { Modifier.styleModifier { property("stroke-dashoffset", "0") } }
        100.percent { Modifier.styleModifier { property("stroke-dashoffset", HomeArt.WIRE_FLOW_SHIFT) } }
    }
    ctx.stylesheet.registerKeyframes("hm-grow") {
        0.percent { Modifier.styleModifier { property("transform", "scaleY(0)") } }
        100.percent { Modifier.styleModifier { property("transform", "scaleY(1)") } }
    }

    ctx.rule(".${HomeClasses.ROOT}") {
        flexColumn(Spacing.lg.toString())
        property("width", "100%")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.PANEL}") {
        property("position", "relative")
        property("min-width", "0")
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("border-radius", Radius.lg.toString())
    }
    ctx.rule(".${HomeClasses.PAD}") {
        property("padding", Spacing.lg.toString())
        flexColumn(Spacing.md.toString())
    }

    ctx.rule(".${HomeClasses.HERO}") {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1fr)")
        property("overflow", "hidden")
    }
    ctx.mediaRule(".${HomeClasses.HERO}", HomeArt.HERO_SPLIT_MIN) {
        property("grid-template-columns", "minmax(0, 5fr) minmax(0, 6fr)")
    }
    ctx.rule(".${HomeClasses.HERO_COPY}") {
        property("padding", Spacing.lg.toString())
        flexColumn(Spacing.md.toString())
        property("justify-content", "center")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.ART}") {
        property("position", "relative")
        property("overflow", "hidden")
        property("min-width", "0")
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    ctx.rule(".${HomeClasses.HERO} .${HomeClasses.ART}") {
        property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
    }
    ctx.mediaRule(".${HomeClasses.HERO} .${HomeClasses.ART}", HomeArt.HERO_SPLIT_MIN) {
        property("border-top", "none")
        property("border-left", "1px solid ${Palette.borderSubtle.cssRgb()}")
    }

    ctx.rule(".${HomeClasses.STATS}") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", Spacing.sm.toString())
    }
    ctx.rule(".${HomeClasses.STAT}") {
        flexColumn(Spacing.xxs.toString())
        property("padding", "${Spacing.xs} 0")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.STAT_VALUE}") {
        property("font-family", Typography.FONT_SANS)
        property("letter-spacing", Tracking.tight.toString())
        property("font-size", "clamp(1.25rem, 3.4vw, 1.75rem)")
        property("font-weight", FontWeight.medium.toString())
        property("line-height", "1.1")
        property("color", Palette.textPrimary.cssRgb())
        property("font-variant-numeric", "tabular-nums")
    }

    ctx.rule(".${HomeClasses.FLOW}") {
        property("display", "grid")
        property("grid-template-columns", "auto minmax(1.25rem, 1fr) auto minmax(1.25rem, 1fr) auto")
        property("align-items", "center")
        property("width", "100%")
        property("max-width", "34rem")
        property("margin", "0 auto")
        property("padding", "${Spacing.xl} ${Spacing.lg} ${Spacing.x3l}")
        property("box-sizing", "border-box")
    }
    ctx.rule(".${HomeClasses.FLOW_INPUTS}") {
        property("display", "grid")
        property("grid-auto-rows", "3rem")
        property("row-gap", "3.75rem")
    }
    ctx.rule(".${HomeClasses.NODE}") {
        property("position", "relative")
        property("display", "flex")
        property("justify-content", "center")
        property("width", "3rem")
        property("height", "3rem")
    }
    ctx.rule(".${HomeClasses.NODE_HUB}") {
        property("width", "4rem")
        property("height", "4rem")
    }
    ctx.rule(".${HomeClasses.CHIP}") {
        centered()
        property("width", "100%")
        property("height", "100%")
        property("box-sizing", "border-box")
        property("background-color", BrandHue.CHIP)
        property("border-radius", Radius.lg.toString())
        property(
            "box-shadow",
            "0 0 0 0.0625rem ${BrandHue.CHIP_RING}, 0 0.25rem 0.75rem ${mix(Palette.textPrimary.cssRgb(), HomeArt.TINT_FAINT)}",
        )
        property("transition", Motion.transition(Motion.DUR_BASE, "opacity", "filter", "box-shadow", "transform"))
    }
    ctx.rule(".${HomeClasses.CHIP} img") {
        property("width", "56%")
        property("height", "56%")
        property("object-fit", "contain")
    }
    ctx.rule(".${HomeClasses.NODE_ON} .${HomeClasses.CHIP}") {
        property("box-shadow", "0 0 0 0.0625rem ${BrandHue.CHIP_RING}, 0 0 0 0.3125rem ${tint(HomeArt.TINT_SOFT)}")
    }
    ctx.rule(".${HomeClasses.NODE_OFF} .${HomeClasses.CHIP}") {
        property("opacity", "0.5")
        property("filter", "grayscale(1)")
        property("box-shadow", "none")
        property("outline", "0.09375rem dashed ${Palette.textTertiary.cssRgb()}")
        property("outline-offset", "0.1875rem")
    }
    ctx.rule(".${HomeClasses.LABEL}") {
        property("position", "absolute")
        property("top", "calc(100% + 0.5rem)")
        property("left", "50%")
        property("transform", "translateX(-50%)")
        flexColumn(Spacing.xxs.toString())
        property("align-items", "center")
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("color", Palette.textSecondary.cssRgb())
        property("white-space", "nowrap")
    }
    ctx.rule(".${HomeClasses.CONNECT}") {
        property("padding", "0.0625rem ${Spacing.xs}")
        property("border-radius", Radius.pill.toString())
        property("border", "1px dashed ${Palette.accentBorder.cssRgb()}")
        property("background-color", Palette.accentSubtle.cssRgb())
        property("color", Palette.accentText.cssRgb())
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("text-decoration", "none")
        property("cursor", "pointer")
        property("transition", Motion.TRANSITION_BASE)
    }
    ctx.hoverRule(".${HomeClasses.CONNECT}") {
        property("background-color", Palette.accentBorder.cssRgb())
    }
    ctx.rule(".${HomeClasses.DOT}") {
        property("position", "absolute")
        property("right", "-0.25rem")
        property("bottom", "-0.25rem")
        property("width", "0.875rem")
        property("height", "0.875rem")
        property("border-radius", "50%")
        property("border", "2px solid ${BrandHue.CHIP}")
        property("box-sizing", "border-box")
    }
    ctx.rule(".${HomeClasses.DOT_OK}") { property("background-color", Palette.status(StatusHue.Success).solid.cssRgb()) }
    ctx.rule(".${HomeClasses.DOT_WARN}") { property("background-color", Palette.status(StatusHue.Warning).solid.cssRgb()) }
    ctx.rule(".${HomeClasses.DOT_BAD}") { property("background-color", Palette.status(StatusHue.Danger).solid.cssRgb()) }

    ctx.rule(".${HomeClasses.WIRE_CELL}") {
        property("position", "relative")
        property("align-self", "stretch")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.WIRE_CELL_OUT}") {
        property("align-self", "center")
        property("height", "2rem")
    }
    ctx.rule(".${HomeClasses.WIRES}") {
        property("position", "absolute")
        property("inset", "0")
        property("display", "block")
        property("width", "100%")
        property("height", "100%")
        property("overflow", "visible")
    }
    ctx.rule(".${HomeClasses.WIRE}") {
        property("fill", "none")
        property("vector-effect", "non-scaling-stroke")
        property("stroke-linecap", "round")
        property("transition", Motion.transition(HomeArt.DUR_WIRE_FADE, "stroke", "opacity"))
    }
    ctx.rule(".${HomeClasses.WIRE_ON}") {
        property("stroke", Palette.accent.cssRgb())
        property("opacity", "0.38")
    }
    ctx.rule(".${HomeClasses.WIRE_OFF}") {
        property("stroke", Palette.textTertiary.cssRgb())
        property("stroke-dasharray", HomeArt.WIRE_DASH_OFF)
        property("opacity", "0.6")
    }
    ctx.rule(".${HomeClasses.WIRE_FLOW}") {
        property("stroke", Palette.accent.cssRgb())
        property("stroke-dasharray", HomeArt.WIRE_DASH_FLOW)
        property("animation", "hm-flow ${HomeArt.DUR_FLOW} linear infinite")
    }

    ctx.rule(".${HomeClasses.SETUP}") {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", Spacing.lg.toString())
        property("padding", Spacing.lg.toString())
    }
    ctx.mediaRule(".${HomeClasses.SETUP}", HomeArt.HERO_SPLIT_MIN) {
        property("grid-template-columns", "minmax(0, 15rem) minmax(0, 1fr)")
    }
    ctx.rule(".${HomeClasses.SETUP_SIDE}") {
        flexColumn(Spacing.md.toString())
        property("align-items", "flex-start")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.RING}") {
        property("position", "relative")
        property("width", "${HomeArt.RING_SIZE}px")
        property("height", "${HomeArt.RING_SIZE}px")
        property("flex-shrink", "0")
    }
    ctx.rule(".${HomeClasses.RING} svg") {
        property("display", "block")
        property("transform", "rotate(-90deg)")
    }
    ctx.rule(".${HomeClasses.RING_TRACK}") {
        property("fill", "none")
        property("stroke", Palette.borderSubtle.cssRgb())
    }
    ctx.rule(".${HomeClasses.RING_FILL}") {
        property("fill", "none")
        property("stroke", Palette.accent.cssRgb())
        property("stroke-linecap", "round")
        property("transition", Motion.transitionWith(HomeArt.DUR_RING, Motion.EASE_EMPHASIS, "stroke-dashoffset"))
    }
    ctx.rule(".${HomeClasses.RING_LABEL}") {
        property("position", "absolute")
        property("inset", "0")
        centered()
        property("font-family", Typography.FONT_MONO)
        property("font-size", Typography.h2.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("color", Palette.textPrimary.cssRgb())
        property("font-variant-numeric", "tabular-nums")
    }
    ctx.rule(".${HomeClasses.MILESTONES}") {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", Spacing.md.toString())
        property("min-width", "0")
    }
    ctx.mediaRule(".${HomeClasses.MILESTONES}", HomeArt.HERO_SPLIT_MIN) {
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
    }
    ctx.rule(".${HomeClasses.MILESTONE}") {
        flexColumn(Spacing.sm.toString())
        property("min-width", "0")
        property("padding", Spacing.md.toString())
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("border-radius", Radius.lg.toString())
    }
    ctx.rule(".${HomeClasses.MILESTONE_ACTIVE}") {
        property("border-color", Palette.accentBorder.cssRgb())
    }
    ctx.rule(".${HomeClasses.ICON_TILE}") {
        centered()
        property("width", "1.5rem")
        property("height", "1.5rem")
        property("flex-shrink", "0")
        property("color", Palette.textSecondary.cssRgb())
    }
    ctx.rule(".${HomeClasses.STEP_TEXT}") {
        flexColumn("0")
        property("flex", "1 1 8rem")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.STEP}") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", Spacing.xs.toString())
        property("min-width", "0")
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textPrimary.cssRgb())
    }
    ctx.rule(".${HomeClasses.MARK}") {
        centered()
        property("width", "1.25rem")
        property("height", "1.25rem")
        property("flex-shrink", "0")
        property("border-radius", "50%")
        property("box-sizing", "border-box")
    }
    ctx.rule(".${HomeClasses.MARK_DONE}") {
        property("background-color", Palette.status(StatusHue.Success).subtle.cssRgb())
        property("color", Palette.status(StatusHue.Success).solid.cssRgb())
    }
    ctx.rule(".${HomeClasses.MARK_TODO}") {
        property("border", "0.09375rem dashed ${Palette.accent.cssRgb()}")
    }
    ctx.rule(".${HomeClasses.MARK_OPTIONAL}") {
        property("border", "0.09375rem dotted ${Palette.textTertiary.cssRgb()}")
    }
    ctx.rule(".${HomeClasses.MARK_BAD}") {
        property("background-color", Palette.status(StatusHue.Danger).subtle.cssRgb())
        property("color", Palette.status(StatusHue.Danger).solid.cssRgb())
    }
    ctx.rule(".${HomeClasses.MARK_WARN}") {
        property("background-color", Palette.status(StatusHue.Warning).subtle.cssRgb())
        property("color", Palette.status(StatusHue.Warning).solid.cssRgb())
    }

    ctx.rule(".${HomeClasses.LIVE}") {
        property("display", "grid")
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", Spacing.md.toString())
        property("align-items", "stretch")
    }
    ctx.mediaRule(".${HomeClasses.LIVE}", HomeArt.LIVE_MD_MIN) {
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
    }
    ctx.mediaRule(".${HomeClasses.LIVE}", HomeArt.LIVE_LG_MIN) {
        property("grid-template-columns", "repeat(12, minmax(0, 1fr))")
    }
    for ((cls, span) in listOf(HomeClasses.SPAN_4 to "4", HomeClasses.SPAN_5 to "5", HomeClasses.SPAN_7 to "7")) {
        ctx.mediaRule(".$cls", HomeArt.LIVE_LG_MIN) { property("grid-column", "span $span") }
    }
    ctx.rule(".${HomeClasses.SPAN_12}") { property("grid-column", "1 / -1") }
    ctx.mediaRule(".${HomeClasses.SPAN_7}", HomeArt.LIVE_MD_MIN) { property("grid-column", "1 / -1") }
    ctx.mediaRule(".${HomeClasses.SPAN_7}", HomeArt.LIVE_LG_MIN) { property("grid-column", "span 7") }

    ctx.rule(".${HomeClasses.CARD_HEAD}") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", Spacing.sm.toString())
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.BIG_TIME}") {
        property("font-family", Typography.FONT_SANS)
        property("font-size", "clamp(1.75rem, 5.6vw, 2.5rem)")
        property("font-weight", FontWeight.medium.toString())
        property("letter-spacing", Tracking.tight.toString())
        property("line-height", "1.1")
        property("color", Palette.textPrimary.cssRgb())
        property("font-variant-numeric", "tabular-nums")
    }
    ctx.rule(".${HomeClasses.DAYS}") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", Spacing.xs.toString())
    }
    ctx.rule(".${HomeClasses.DAY}") {
        centered()
        property("width", "2rem")
        property("height", "2rem")
        property("border-radius", "50%")
        property("font-family", Typography.FONT_MONO)
        property("font-size", Typography.caption.size.toString())
        property("color", Palette.textTertiary.cssRgb())
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("transition", Motion.TRANSITION_BASE)
    }
    ctx.rule(".${HomeClasses.DAY_ON}") {
        property("color", Palette.accentText.cssRgb())
        property("background-color", Palette.accentSubtle.cssRgb())
        property("box-shadow", "inset 0 0 0 0.0625rem ${Palette.accentBorder.cssRgb()}")
    }
    ctx.rule(".${HomeClasses.RUN}") {
        property("display", "grid")
        property("grid-template-columns", "auto minmax(0, 1fr) auto")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
        property("padding", Spacing.sm.toString())
        property("border-radius", Radius.control.toString())
        property("color", "inherit")
        property("text-decoration", "none")
        property("cursor", "pointer")
        property("transition", Motion.TRANSITION_BASE)
    }
    ctx.hoverRule(".${HomeClasses.RUN}") { property("background-color", Palette.active.cssRgb()) }

    ctx.rule(".${HomeClasses.BARS}") {
        property("display", "flex")
        property("align-items", "flex-end")
        property("gap", Spacing.xxs.toString())
        property("height", "6.5rem")
    }
    ctx.rule(".${HomeClasses.BAR}") {
        property("flex", "1 1 0")
        property("display", "flex")
        property("align-items", "flex-end")
        property("height", "100%")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.BAR_FILL}") {
        property("width", "100%")
        property("height", "var(${HomeArt.PROGRESS_VAR})")
        property("min-height", "0.25rem")
        property("border-radius", Radius.inner.toString())
        property("background-color", mix(Palette.accent.cssRgb(), "70%"))
        property("transform-origin", "bottom")
        property(
            "animation",
            "hm-grow ${HomeArt.DUR_BAR_GROW} ${Motion.EASE_EMPHASIS} backwards",
        )
        property("animation-delay", "calc(var(${HomeArt.INDEX_VAR}) * ${Motion.STAGGER_STEP_MS}ms)")
    }
    ctx.rule(".${HomeClasses.BAR_ZERO}") {
        property("background-color", Palette.borderSubtle.cssRgb())
    }
    ctx.rule(".${HomeClasses.BAR_TODAY}") {
        property("background-color", Palette.accent.cssRgb())
    }

    ctx.rule(".${HomeClasses.STACK}") {
        property("display", "flex")
        property("align-items", "center")
        property("padding-left", "0.625rem")
    }
    ctx.rule(".${HomeClasses.STACK_ITEM}") {
        property("margin-left", "-0.625rem")
        property("border-radius", "50%")
        property("box-shadow", "0 0 0 0.125rem ${Palette.panel.cssRgb()}")
        property("transition", Motion.TRANSITION_HOVER)
    }
    ctx.hoverRule(".${HomeClasses.STACK_ITEM}") { property("transform", "translateY(${Marketing.LIFT_DISTANCE})") }
    ctx.rule(".${HomeClasses.STACK_MORE}") {
        centered()
        width36()
        property("font-family", Typography.FONT_MONO)
        property("font-size", Typography.caption.size.toString())
        property("color", Palette.textSecondary.cssRgb())
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    ctx.rule(".${HomeClasses.GHOST_AVATAR}") {
        width36()
        property("border-radius", "50%")
        property("border", "0.09375rem dashed ${Palette.textTertiary.cssRgb()}")
        property("opacity", "0.7")
        property("box-sizing", "border-box")
        property("background-color", Palette.panel.cssRgb())
    }

    ctx.rule(".${HomeClasses.TILES}") {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", Spacing.sm.toString())
    }
    ctx.rule(".${HomeClasses.TILE}") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
        property("min-width", "0")
        property("padding", Spacing.sm.toString())
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("border-radius", Radius.lg.toString())
        property("color", Palette.textPrimary.cssRgb())
        property("font-size", Typography.bodySmall.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("text-decoration", "none")
        property("cursor", "pointer")
        property("transition", Motion.TRANSITION_HOVER)
    }
    ctx.hoverRule(".${HomeClasses.TILE}") {
        lift()
        property("border-color", Palette.border.cssRgb())
    }

    ctx.rule(".${HomeClasses.EMPTY}") {
        flexColumn(Spacing.sm.toString())
        property("align-items", "center")
        property("text-align", "center")
        property("padding", "${Spacing.md} 0")
        property("flex", "1")
        property("justify-content", "center")
    }
    ctx.rule(".${HomeClasses.EMPTY_ART}") {
        flexColumn(Spacing.xs.toString())
        property("width", "100%")
        property("max-width", "16rem")
        property("padding", Spacing.md.toString())
        property("box-sizing", "border-box")
        property("border-radius", Radius.lg.toString())
        property("border", "1px dashed ${Palette.border.cssRgb()}")
        washedArt()
    }
    ctx.rule(".${HomeClasses.GHOST}") {
        property("background-color", Palette.borderSubtle.cssRgb())
        property("border-radius", Radius.inner.toString())
    }
    ctx.rule(".${HomeClasses.GHOST_ACCENT}") {
        property("background-color", Palette.accentBorder.cssRgb())
        property("opacity", "0.6")
    }
    ctx.rule(".${HomeClasses.BONE}") {
        property("background-color", Palette.borderSubtle.cssRgb())
        property("border-radius", Radius.inner.toString())
        property("flex-shrink", "0")
    }
    ctx.rule(".${HomeClasses.STRIP_END}") {
        property("margin-left", "auto")
    }
    ctx.rule(".${HomeClasses.CHANNEL}") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", Spacing.xxs.toString())
        property("max-width", "100%")
        property("padding", "0.0625rem ${Spacing.xs}")
        property("border-radius", Radius.pill.toString())
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("color", Palette.textSecondary.cssRgb())
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        truncate()
    }
    ctx.rule(".${HomeClasses.CHANNEL} img") {
        property("width", "0.75rem")
        property("height", "0.75rem")
        property("flex-shrink", "0")
    }
    ctx.rule(".${HomeClasses.ROW_TEXT}") {
        flexColumn("0")
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.ROW_TEXT} > *") {
        property("display", "block")
        truncate()
    }
    ctx.rule(".${HomeClasses.TILE_LABEL}") {
        property("min-width", "0")
        property("line-height", "1.3")
    }
    ctx.rule(".${HomeClasses.STACK_FACTS}") {
        flexColumn(Spacing.xxs.toString())
        property("min-width", "0")
    }
    ctx.rule(".${HomeClasses.STRIP}") {
        property("display", "flex")
        property("align-items", "center")
        property("flex-wrap", "wrap")
        property("gap", Spacing.sm.toString())
        property("padding", "${Spacing.sm} ${Spacing.lg}")
    }

    ctx.reducedMotionRule(".${HomeClasses.WIRE_FLOW}") { property("animation", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.BAR_FILL}") { property("animation", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.RING_FILL}") { property("transition", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.LIFT}") { property("transform", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.MILESTONE}") { property("transform", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.TILE}") { property("transform", "none !important") }
    ctx.reducedMotionRule(".${HomeClasses.STACK_ITEM}") { property("transform", "none !important") }
}

private fun StyleScope.width36() {
    property("width", "${HomeArt.AVATAR_PX}px")
    property("height", "${HomeArt.AVATAR_PX}px")
    property("flex-shrink", "0")
}
